import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  CHART_COLORS,
  CHART_FONT_CONFIGS,
  CHART_LEGEND_CONFIG,
  CHART_TOOLTIP_CONFIG,
  STAKEHOLDER_SUPPORT_COLORS,
} from '@/src/lib/chartConfig.js';
import { WidgetWrapper } from '@/src/components/projects/ProjectDashboard/WidgetWrapper.jsx';
import { WidgetInner } from '@/src/components/projects/ProjectDashboard/WidgetInner.jsx';
import Empty from '@/src/components/Empty.jsx';
import { stakeholderSupportDistributionWidgetContent } from '@/src/components/EmptyStates.jsx';
import { buildPath } from '@/src/components/Link.jsx';
import { STAKEHOLDERS_INDEX } from '@/src/constants/routes.js';

export const StakeholderSupportWidget = ({ data }) => {
  const navigate = useNavigate();
  const { slug, projectId } = useParams();

  const handleNavigateToStakeholders = () => {
    const path = buildPath(STAKEHOLDERS_INDEX, { slug, projectId });
    navigate(path);
  };

  const hasData = data && Object.values(data).some((val) => val > 0);

  const dataSet = {
    labels: [
      'Very Supportive',
      'Supportive',
      'Neutral',
      'Unsupportive',
      'Very Unsupportive',
    ],
    datasets: [
      {
        data: [
          data?.verySupportive || 0,
          data?.supportive || 0,
          data?.neutral || 0,
          data?.unsupportive || 0,
          data?.veryUnsupportive || 0,
        ],
        backgroundColor: [
          STAKEHOLDER_SUPPORT_COLORS.verySupportive,
          STAKEHOLDER_SUPPORT_COLORS.supportive,
          STAKEHOLDER_SUPPORT_COLORS.neutral,
          STAKEHOLDER_SUPPORT_COLORS.unsupportive,
          STAKEHOLDER_SUPPORT_COLORS.veryUnsupportive,
        ],
        borderRadius: 0,
        barThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: true,
          tickLength: 5,
          color: CHART_COLORS.axisBorder,
        },
        border: {
          display: true,
          color: CHART_COLORS.axisBorder,
          width: CHART_COLORS.axisBorderWidth,
        },
        ticks: {
          font: CHART_FONT_CONFIGS.regular,
          color: CHART_COLORS.textMuted,
        },
      },
      y: {
        grid: {
          drawOnChartArea: false,
          drawTicks: true,
          tickLength: 5,
          color: CHART_COLORS.axisBorder,
        },
        border: {
          display: true,
          color: CHART_COLORS.axisBorder,
          width: CHART_COLORS.axisBorderWidth,
        },
        ticks: {
          font: CHART_FONT_CONFIGS.regular,
          color: CHART_COLORS.textMuted,
          stepSize: 1,
          precision: 0,
        },
      },
    },
    plugins: {
      legend: {
        ...CHART_LEGEND_CONFIG,
        labels: {
          ...CHART_LEGEND_CONFIG.labels,
          generateLabels: (chart) => {
            return chart.data.labels.map((label, i) => ({
              text: label,
              fillStyle: chart.data.datasets[0].backgroundColor[i],
              strokeStyle: chart.data.datasets[0].backgroundColor[i],
              lineWidth: 0,
              pointStyle: 'rect',
              hidden: false,
              index: i,
              fontColor: CHART_COLORS.textMuted,
            }));
          },
        },
      },
      tooltip: CHART_TOOLTIP_CONFIG,
    },
  };

  return (
    <WidgetWrapper className="stakeholder-support-widget">
      <h2 className="m-b-18">Stakeholder Support Distribution</h2>
      {hasData ? (
        <WidgetInner height={500}>
          <div className="p-y-24">
            <div style={{ height: '400px', width: '100%' }}>
              <Bar data={dataSet} options={options} />
            </div>
          </div>
        </WidgetInner>
      ) : (
        <WidgetInner height={500} className="flex flex-col">
          <Empty
            simplified
            content={stakeholderSupportDistributionWidgetContent}
            action={handleNavigateToStakeholders}
          />
        </WidgetInner>
      )}
    </WidgetWrapper>
  );
};
